import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CardDonationPost from '@/domains/mobile-web/components/CardDonationPost/CardDonationPost';
import PostComposer, {
  ComposerActionBar,
} from '@/domains/mobile-web/components/PostComposer/PostComposer';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCreateBookRequestComment } from '@/domains/mobile-web/hooks/useBookRequests';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import { useComposerUser } from '@/domains/mobile-web/hooks/useComposerUser';
import { useCreateKidsPostComment } from '@/domains/mobile-web/hooks/useKidsPosts';
import type { BookRequest } from '@/domains/mobile-web/types/bookRequest.types';
import type { KidsPost } from '@/domains/mobile-web/types/kidsPost.types';
import { toDonationPost } from '@/domains/mobile-web/utils/bookRequest';
import { toMediaPost } from '@/domains/mobile-web/utils/kidsPost';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

// The post comes from the timeline (there is no single-post endpoint)
type ReplyLocationState =
  | { from?: 'donation'; post?: BookRequest; returnBack?: boolean }
  | { from: 'media'; post?: KidsPost; returnBack?: boolean };

// Comment limit of both boards (characters, after trimming); comments are text only
const MAX_COMMENT_LENGTH = 500;

const Reply = () => {
  const navigate = useNavigate();
  const cartCount = useCartCount();
  const location = useLocation();
  const state = location.state as ReplyLocationState | null;
  const isMedia = state?.from === 'media';
  const backPath = isMedia ? '/store/media' : '/store/donation';
  const composerUser = useComposerUser();
  const createDonationComment = useCreateBookRequestComment();
  const createMediaComment = useCreateKidsPostComment();
  const createComment = isMedia ? createMediaComment : createDonationComment;

  const { id } = useParams<{ id: string }>();
  const [content, setContent] = useState('');

  const post =
    !state?.post || state.post.id !== id
      ? undefined
      : state.from === 'media'
        ? toMediaPost(state.post)
        : toDonationPost(state.post);

  const handleSubmit = () => {
    if (!content.trim() || !post || createComment.isPending) return;
    createComment.mutate(
      { postId: post.id, content: content.trim() },
      // returnBack: opened from a detail page, which keeps its own post in history
      { onSuccess: () => (state?.returnBack ? navigate(-1) : navigate(backPath)) }
    );
  };

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          totalProduct={cartCount}
          title={isMedia ? 'Media (Foto Anak)' : 'Donasi'}
          onAction={() => navigate('/store/cart')}
        />
        <ComposerActionBar
          disabled={createComment.isPending}
          onCancel={() => navigate(-1)}
          onSubmit={handleSubmit}
        />
      </HeaderMobile>

      <WrapperMobile className="pt-3">
        {post ? (
          <>
            <CardDonationPost post={post} showComments={false} showMedia={isMedia} />
            <PostComposer
              user={composerUser}
              value={content}
              onChange={setContent}
              replyTo={post.user}
              maxLength={MAX_COMMENT_LENGTH}
              placeholder="Tuliskan balasan anda"
            />
          </>
        ) : (
          <p className="reg12 text-text-grey text-center py-6">Postingan tidak ditemukan.</p>
        )}
      </WrapperMobile>
    </main>
  );
};

export default Reply;
